import { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ParticipantEditor } from "@/components/participants/ParticipantEditor";
import type { ContactRecord } from "@/hooks/useContacts";
import { ParticipantDraft, normaliseEmail, ScriptFormParticipantSummary } from "@/types/participants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { syncParticipantsForScriptForm } from "@/lib/participants";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Mail, Share2, Copy } from "lucide-react";
import { copyTextToClipboard, resolvePublicBaseUrl, shareViaNavigator, supportsNativeShare } from "@/utils/share";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { getErrorMessage } from "@/utils/errors";
import { LanguageToggle } from "@/components/ui/LanguageToggle";
import { useTranslation } from "@/i18n/useTranslation";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ShareScriptFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scriptFormId: string | null;
  formName: string;
  movieName: string;
  reviewLink: string | null;
  currentDueDate: string | null;
  password?: string | null;
  participants: ScriptFormParticipantSummary[];
  contacts: ContactRecord[];
  refreshContacts: () => Promise<void>;
  onParticipantsUpdated: () => Promise<void> | void;
}

export const ShareScriptFormDialog = ({
  open,
  onOpenChange,
  scriptFormId,
  formName,
  movieName,
  reviewLink,
  currentDueDate,
  password,
  participants,
  contacts,
  refreshContacts,
  onParticipantsUpdated,
}: ShareScriptFormDialogProps) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [localParticipants, setLocalParticipants] = useState<ParticipantDraft[]>([]);
  const [pendingContacts, setPendingContacts] = useState<ContactRecord[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationRecipients, setConfirmationRecipients] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<string>(currentDueDate ? new Date(currentDueDate).toISOString().split('T')[0] : "");

  useEffect(() => {
    setDueDate(currentDueDate ? new Date(currentDueDate).toISOString().split('T')[0] : "");
  }, [currentDueDate, open]);

  const saveDueDate = async (date: string) => {
    if (!scriptFormId) return;

    const { error } = await supabase
      .from("script_forms")
      .update({ due_date: date || null })
      .eq("id", scriptFormId);

    if (error) {
      toast({
        title: "Error saving due date",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Due date saved",
        description: "The due date has been updated.",
      });
      onParticipantsUpdated(); // Refresh parent data
    }
  };

  const reviewUrl = useMemo(() => {
    if (!reviewLink) return "";
    const baseUrl = resolvePublicBaseUrl();
    const origin = baseUrl || (typeof window !== "undefined" ? window.location.origin : "");
    return origin ? `${origin}/review/${reviewLink}` : `/review/${reviewLink}`;
  }, [reviewLink]);

  useEffect(() => {
    if (!open) {
      setLocalParticipants([]);
      setPendingContacts([]);
      setIsSaving(false);
      setIsSharing(false);
      setConfirmationOpen(false);
      setConfirmationRecipients([]);
      return;
    }

    const mapped = participants.map((participant) => {
      const email = normaliseEmail(participant.contacts?.email ?? "");
      return {
        key: `${participant.contact_id}-${participant.id}`,
        contactId: participant.contact_id,
        email,
        name: participant.contacts?.name ?? null,
        isNewContact: false,
      } satisfies ParticipantDraft;
    });

    setLocalParticipants(mapped);
  }, [open, participants]);

  const combinedContacts = useMemo(() => {
    const map = new Map<string, ContactRecord>();
    contacts.forEach((contact) => {
      map.set(normaliseEmail(contact.email), contact);
    });
    pendingContacts.forEach((contact) => {
      const key = normaliseEmail(contact.email);
      if (!map.has(key)) {
        map.set(key, contact);
      }
    });
    return Array.from(map.values());
  }, [contacts, pendingContacts]);

  const handleCreatePendingContact = (contact: ContactRecord) => {
    const key = normaliseEmail(contact.email);
    setPendingContacts((previous) => {
      if (previous.some((existing) => normaliseEmail(existing.email) === key)) {
        return previous;
      }
      if (contacts.some((existing) => normaliseEmail(existing.email) === key)) {
        return previous;
      }
      return [...previous, contact];
    });
  };

  const persistParticipants = async () => {
    if (!scriptFormId) return [] as string[];

    await syncParticipantsForScriptForm(
      scriptFormId,
      localParticipants.map((participant) => ({
        contactId: participant.contactId,
        email: participant.email,
        name: participant.name,
      })),
    );

    await refreshContacts();
    await onParticipantsUpdated();

    return Array.from(
      new Set(localParticipants.map((participant) => normaliseEmail(participant.email))),
    );
  };

  const handleCopyLink = async () => {
    if (!reviewUrl) return;
    try {
      await copyTextToClipboard(reviewUrl);
      toast({
        title: t("share.toast.copy_success_title"),
        description: t("share.toast.copy_success_description"),
      });
    } catch (error) {
      toast({
        title: t("share.toast.copy_failure_title"),
        description: t("share.toast.copy_failure_description"),
        variant: "destructive",
      });
    }
  };

  const handleShareNative = async () => {
    if (!reviewUrl) return;
    setIsSharing(true);
    try {
      await persistParticipants();
      await shareViaNavigator({
        movieName,
        formName,
        url: reviewUrl,
        password,
      });
    } catch (error: unknown) {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        toast({
          title: t("share.toast.share_failure_title"),
          description: getErrorMessage(error, t("share.toast.share_failure_description")),
          variant: "destructive",
        });
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleSendEmail = async () => {
    if (!reviewUrl) return;
    if (!scriptFormId) return;

    setIsSaving(true);
    try {
      const recipients = await persistParticipants();

      if (recipients.length === 0) {
        throw new Error(t("share.toast.email_no_recipients_description"));
      }

      const response = await fetch("/api/send-share-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          movieName,
          formName,
          reviewUrl,
          password,
          dueDate: dueDate || null,
          recipients,
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          getErrorMessage(
            payload?.error ?? payload?.message ?? t("share.toast.email_failure_description"),
            t("share.toast.email_failure_description"),
          ),
        );
      }

      setConfirmationRecipients(recipients);
      setConfirmationOpen(true);
    } catch (error: unknown) {
      toast({
        title: t("share.toast.email_failure_title"),
        description: getErrorMessage(error, t("share.toast.email_failure_description")),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side={isDesktop ? "right" : "bottom"}
          className="sm:max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          <SheetHeader>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:pr-12">
              <div>
                <SheetTitle>{t("share.dialog.title", { formName })}</SheetTitle>
                <SheetDescription>
                  {t("share.dialog.description_prefix")}
                  <span className="font-medium text-foreground">{movieName}</span>
                  {t("share.dialog.description_suffix")}
                </SheetDescription>
              </div>
              <LanguageToggle className="sm:self-start" />
            </div>
          </SheetHeader>

          <div className="space-y-4">
            {reviewUrl && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">{t("share.dialog.review_link_label")}</p>
                <p className="text-xs font-mono break-all bg-muted/60 px-3 py-2 rounded-md">{reviewUrl}</p>
              </div>
            )}

            {password && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t("share.dialog.password_label")}</p>
                <p className="text-sm font-medium">{password}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="due-date">Due Date</Label>
              <Input
                id="due-date"
                type="date"
                value={dueDate}
                onChange={(e) => {
                  setDueDate(e.target.value);
                  saveDueDate(e.target.value);
                }}
              />
            </div>

            <ParticipantEditor
              participants={localParticipants}
              onChange={setLocalParticipants}
              availableContacts={combinedContacts}
              onCreatePendingContact={handleCreatePendingContact}
            />
          </div>

          <div className="mt-6 space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("share.dialog.quick_actions")}
            </p>
            <SheetFooter className="flex flex-col gap-3 rounded-lg border border-border/60 bg-muted/30 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button variant="outline" onClick={handleCopyLink} className="w-full sm:w-auto">
                  <Copy className="mr-2 h-4 w-4" />
                  {t("share.dialog.copy_link")}
                </Button>
                {supportsNativeShare() && (
                  <Button
                    variant="outline"
                    onClick={handleShareNative}
                    disabled={isSharing}
                    className="w-full sm:w-auto"
                  >
                    {isSharing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Share2 className="mr-2 h-4 w-4" />}
                    {t("share.dialog.share_native")}
                  </Button>
                )}
              </div>
              <Button onClick={handleSendEmail} disabled={isSaving || !scriptFormId} className="w-full sm:w-auto">
                {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                {t("share.dialog.send_email")}
              </Button>
            </SheetFooter>
          </div>
        </SheetContent>
      </Sheet>
      <AlertDialog open={confirmationOpen} onOpenChange={setConfirmationOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("share.dialog.email_confirmation_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("share.dialog.email_confirmation_description", { count: confirmationRecipients.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirmationRecipients.length > 0 && (
            <div className="rounded-md border border-border/60 bg-muted/30 p-3 text-sm">
              <p className="mb-2 font-medium text-muted-foreground">
                {t("share.dialog.email_confirmation_recipients_label")}
              </p>
              <ul className="list-inside list-disc space-y-1 break-all">
                {confirmationRecipients.map((recipient) => (
                  <li key={recipient}>{recipient}</li>
                ))}
              </ul>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                setConfirmationOpen(false);
                onOpenChange(false);
              }}
            >
              {t("share.dialog.email_confirmation_primary")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
